import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 274 — the three internal department task lists.
export enum DepartmentTeam {
  Sales = 0,
  Development = 1,
  Deployment = 2,
  Accounting = 3, // item 376 — the accountant page's own list
  Admin = 4, // item 477 — the ADMIN-ROLE list: adding a task notifies ALL admins
}

export interface DepartmentTask {
  id: string;
  department: DepartmentTeam;
  text: string;
  isDone: boolean;
  // item 339 — CSV of assigned user ids; avatars render on the row
  assigneeUserIds?: string | null;
  createdDate: string;
  // item 399 — recurrence (completing respawns the next occurrence, due one cadence out)
  recurrence?: TaskRecurrence;
  dueDate?: string | null;
  // item 1463 — optional project tie + the names the API joins in (null = plain department task)
  projectId?: string | null;
  projectName?: string | null;
  customerName?: string | null;
  createdById?: string | null;
  // item 1462 — client-only: an optimistic row the server hasn't confirmed yet
  pending?: boolean;
}

export enum TaskRecurrence {
  None = 0,
  Weekly = 1,
  BiWeekly = 2,
  Monthly = 3,
  Annually = 4,
}

export const RECURRENCE_LABELS: Record<TaskRecurrence, string> = {
  [TaskRecurrence.None]: 'Does not repeat',
  [TaskRecurrence.Weekly]: 'Weekly',
  [TaskRecurrence.BiWeekly]: 'Bi-weekly',
  [TaskRecurrence.Monthly]: 'Monthly',
  [TaskRecurrence.Annually]: 'Annually',
};

// item 339 — CSV helpers for the assignee list
export const parseAssignees = (csv?: string | null): string[] => (csv ? csv.split(',').map((x) => x.trim()).filter(Boolean) : []);

const KEY = 'departmentTasks';

// item 1462 (Chad: "it's really slow when you click and hit Add… not spunky and fast") — the
// board used to wait on the POST, then throw its list away and re-fetch every task, then
// render (two Azure round trips + the admin push fan-out) before a row appeared. Now every
// mutation is OPTIMISTIC: the cache changes the instant you act, the server confirms in the
// background, and a failure rolls the cache back with the error surfaced by the caller. The
// refetch still happens on settle so server-side facts (recurrence respawns, names) catch up.
const useDepartmentTasks = () => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries(KEY);
  const snapshot = () => queryClient.getQueryData<DepartmentTask[]>(KEY) ?? [];
  const write = (next: DepartmentTask[]) => queryClient.setQueryData<DepartmentTask[]>(KEY, next);
  const rollback = (prev: DepartmentTask[]) => () => write(prev);

  const query = useQuery(KEY, async (): Promise<DepartmentTask[]> => {
    const { data } = await axios.get(`${apiUrl()}DepartmentTask`);
    return data;
  });

  const createMutation = useMutation(
    (input: { department: DepartmentTeam; text: string; projectId?: string | null; projectName?: string | null; customerName?: string | null }) => axios.post(`${apiUrl()}DepartmentTask`, {
      department: input.department, text: input.text, projectId: input.projectId ?? null,
    }),
    {
      onMutate: async (input) => {
        await queryClient.cancelQueries(KEY);
        const prev = snapshot();
        const optimistic: DepartmentTask = {
          id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          department: input.department,
          text: input.text,
          isDone: false,
          assigneeUserIds: null,
          createdDate: new Date().toISOString(),
          recurrence: TaskRecurrence.None,
          dueDate: null,
          projectId: input.projectId ?? null,
          projectName: input.projectName ?? null,
          customerName: input.customerName ?? null,
          pending: true,
        };
        write([...prev, optimistic]);
        return rollback(prev);
      },
      onError: (_e, _v, undo) => { if (typeof undo === 'function') undo(); },
      onSettled: invalidate,
    },
  );

  const doneMutation = useMutation(
    (input: { id: string; done: boolean }) => axios.post(`${apiUrl()}DepartmentTask/${input.id}/done`, { done: input.done }),
    {
      onMutate: async (input) => {
        await queryClient.cancelQueries(KEY);
        const prev = snapshot();
        write(prev.map((t) => (t.id === input.id ? { ...t, isDone: input.done } : t)));
        return rollback(prev);
      },
      onError: (_e, _v, undo) => { if (typeof undo === 'function') undo(); },
      onSettled: invalidate,
    },
  );

  const archiveMutation = useMutation(
    (id: string) => axios.delete(`${apiUrl()}DepartmentTask/${id}`),
    {
      onMutate: async (id) => {
        await queryClient.cancelQueries(KEY);
        const prev = snapshot();
        write(prev.filter((t) => t.id !== id));
        return rollback(prev);
      },
      onError: (_e, _v, undo) => { if (typeof undo === 'function') undo(); },
      onSettled: () => { invalidate(); queryClient.invalidateQueries('departmentTasksArchived'); },
    },
  );

  const recurrenceMutation = useMutation(
    (input: { id: string; recurrence: TaskRecurrence }) => axios.post(`${apiUrl()}DepartmentTask/${input.id}/recurrence`, { recurrence: input.recurrence }),
    {
      onMutate: async (input) => {
        await queryClient.cancelQueries(KEY);
        const prev = snapshot();
        write(prev.map((t) => (t.id === input.id ? { ...t, recurrence: input.recurrence } : t)));
        return rollback(prev);
      },
      onError: (_e, _v, undo) => { if (typeof undo === 'function') undo(); },
      onSettled: invalidate,
    },
  );
  // item 339 — assign users to a task (avatars on the row)
  const assigneesMutation = useMutation(
    (input: { id: string; userIds: string[] }) => axios.post(`${apiUrl()}DepartmentTask/${input.id}/assignees`, { userIds: input.userIds }),
    {
      onMutate: async (input) => {
        await queryClient.cancelQueries(KEY);
        const prev = snapshot();
        write(prev.map((t) => (t.id === input.id ? { ...t, assigneeUserIds: input.userIds.join(',') || null } : t)));
        return rollback(prev);
      },
      onError: (_e, _v, undo) => { if (typeof undo === 'function') undo(); },
      onSettled: invalidate,
    },
  );

  // item 1465 — archived tasks (their own section; restore puts one back)
  const ARCHIVED_KEY = 'departmentTasksArchived';
  const archivedQuery = useQuery(ARCHIVED_KEY, async (): Promise<DepartmentTask[]> => {
    const { data } = await axios.get(`${apiUrl()}DepartmentTask/archived`);
    return data;
  });
  const restoreMutation = useMutation(
    (id: string) => axios.post(`${apiUrl()}DepartmentTask/${id}/restore`),
    {
      onMutate: async (id) => {
        await queryClient.cancelQueries(ARCHIVED_KEY);
        const prevArchived = queryClient.getQueryData<DepartmentTask[]>(ARCHIVED_KEY) ?? [];
        const row = prevArchived.find((t) => t.id === id);
        queryClient.setQueryData<DepartmentTask[]>(ARCHIVED_KEY, prevArchived.filter((t) => t.id !== id));
        if (row) write([...snapshot(), { ...row, pending: true }]);
        return () => queryClient.setQueryData<DepartmentTask[]>(ARCHIVED_KEY, prevArchived);
      },
      onError: (_e, _v, undo) => { if (typeof undo === 'function') undo(); },
      onSettled: () => { invalidate(); queryClient.invalidateQueries(ARCHIVED_KEY); },
    },
  );

  return {
    tasks: query.data ?? [],
    status: query.status,
    archivedTasks: archivedQuery.data ?? [],
    archivedStatus: archivedQuery.status,
    restore: (id: string) => restoreMutation.mutateAsync(id),
    // item 1463 — optional project tie; names ride along so the optimistic row can render its chip
    addTask: (department: DepartmentTeam, text: string, project?: { id: string; name?: string | null; customerName?: string | null }) => createMutation.mutateAsync({
      department, text, projectId: project?.id ?? null, projectName: project?.name ?? null, customerName: project?.customerName ?? null,
    }),
    setDone: (id: string, done: boolean) => doneMutation.mutateAsync({ id, done }),
    setRecurrence: (id: string, recurrence: TaskRecurrence) => recurrenceMutation.mutateAsync({ id, recurrence }),
    archive: (id: string) => archiveMutation.mutateAsync(id),
    setAssignees: (id: string, userIds: string[]) => assigneesMutation.mutateAsync({ id, userIds }),
    isBusy: createMutation.isLoading || doneMutation.isLoading || archiveMutation.isLoading,
  };
};

export default useDepartmentTasks;
